import { t } from "@lingui/macro";
import {
  NavigationProp,
  RouteProp,
  useNavigation,
  useRoute,
} from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { add, format } from "date-fns";
import React, { Suspense, useEffect, useState } from "react";
import { NativeScrollEvent, NativeSyntheticEvent } from "react-native";
import { useSharedValue } from "react-native-reanimated";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import {
  facilityToScheduleOption,
  ScheduleOption,
  ScheduleSwitcher,
} from "@/components/ScheduleSwitcher";
import { useGetClubStartOfWeekNumberPatron } from "@/hooks/useGetClubStartOfWeekNumberPatron";
import { useScheduleNavigator } from "@/hooks/useScheduleNavigator";
import { AgendaLayout } from "@whoosh/design-system/src/apps/PatronApp/layout/AgendaLayout";
import { CalendarBody } from "@whoosh/design-system/src/components/controls/CalendarBody/components/CalendarBody";
import { Loader } from "@whoosh/design-system/src/components/display";
import {
  Box,
  ScrollView,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { useBreakpointValue } from "@whoosh/design-system/src/hooks/useBreakpointValue";

import { TeeSheetAgendaHeaderNew } from "../../../../features/TeeSheet/components/TeeSheetAgendaHeaderNew/TeeSheetAgendaHeaderNew";
import { ClubStackNavigatorParamList } from "../../../../navigators/ClubNavigator";
import { HomeTabsNavigatorParamList } from "../../../../navigators/HomeTabsNavigator/types";
import { DiningAgendaFiltersModal } from "../../../BookingDetail/ForYouDiningRemains/components/DiningAgendaFiltersModal";
import {
  DiningAvailability,
  ExistingDiningBooking,
} from "../../../BookingDetail/ForYouDiningRemains/components/DiningAvailability";
import { DiningAgendaScreenQuery } from "./__generated__/DiningAgendaScreenQuery.graphql";
import { DiningAgendaFilters } from "./components/DiningAgendaFilters";

export const DiningAgendaScreen = ({
  existingBooking,
}: {
  existingBooking?: ExistingDiningBooking;
}) => {
  const [queryRef, loadQuery] = useQueryLoader<DiningAgendaScreenQuery>(
    DiningAgendaScreenQueryString
  );

  useEffect(() => {
    loadQuery({}, { fetchPolicy: "store-and-network" });
  }, [loadQuery]);

  return (
    <Suspense fallback={<Loader />}>
      <Box flex={1} safeAreaTop>
        <WhiteBoxToCoverHeaderShadow />
        {queryRef ? (
          <DiningAgendaScreenInternal
            queryRef={queryRef}
            existingBooking={existingBooking}
          />
        ) : null}
      </Box>
    </Suspense>
  );
};

// This is used to cover up the shadow of the header that appears in the status bar.
const WhiteBoxToCoverHeaderShadow = () => {
  return (
    <Box
      height={60}
      style={{ zIndex: 1, marginTop: -60 }}
      bg="surface-primary"
    ></Box>
  );
};

type Props = {
  queryRef: PreloadedQuery<DiningAgendaScreenQuery>;
  existingBooking?: ExistingDiningBooking;
};

export const DiningAgendaScreenInternal = ({
  queryRef,
  existingBooking,
}: Props) => {
  const route =
    useRoute<
      RouteProp<ClubStackNavigatorParamList, "Agenda" | "MoveDiningBooking">
    >();
  const navigation =
    useNavigation<NavigationProp<ClubStackNavigatorParamList>>();

  const homeNavigation =
    useNavigation<NavigationProp<HomeTabsNavigatorParamList>>();

  const date = route.params.date;

  const data = usePreloadedQuery(DiningAgendaScreenQueryString, queryRef);
  const member = data.session?.currentClientProfile?.member;

  const clubWeekStartsOn = useGetClubStartOfWeekNumberPatron(
    member?.club ?? null
  );
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const scrollY = useSharedValue(0);

  const onScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const offsetY = event.nativeEvent.contentOffset.y;
    scrollY.value = offsetY;
    setIsScrolled(offsetY > 1);
  };

  const handleBackPress = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      homeNavigation.navigate("MainHome");
    }
  };

  const facility =
    member?.club?.facilities?.edges && member.club.facilities.edges.length
      ? member.club.facilities.edges.find(
          (e) => e?.node?.department?.type === "DINING"
        )
      : null;
  const firstDiningFacilityId = facility?.node?.id;

  const curDate = new Date();
  const todayString = format(curDate, "yyyy-MM-dd");

  const memberBookingWindowDays = facility?.node?.memberBookingWindowDays || 14;
  const calendarEndOn = format(
    add(new Date(), { days: memberBookingWindowDays }),
    "yyyy-MM-dd"
  );
  useEffect(() => {
    if (date === "today") {
      navigation.setParams({ date: todayString });
    }
  }, [date, navigation, todayString]);

  const isMobile = useBreakpointValue({
    base: true,
    xs: true,
    sm: true,
    md: true,
    lg: false,
  });

  const navigateToSchedule = useScheduleNavigator();

  const onScheduleSwitcherChange = (option: ScheduleOption) => {
    const curDate = new Date();
    const todayString = format(curDate, "yyyy-MM-dd");
    const navigateDate = date ? date : todayString;
    navigateToSchedule(option.type, option.slug, navigateDate);
  };

  const selectedScheduleOption = facilityToScheduleOption(
    facility?.node || null
  );

  return (
    <>
      <AgendaLayout
        shouldCenter={true}
        agenda={
          <ScrollView
            pt={7} // needed to offset the space for course notes
            flex={1}
            onScroll={onScroll}
            scrollEventThrottle={20}
            px={isMobile ? 4 : 12}
            showsVerticalScrollIndicator={!isMobile}
          >
            <Box width="100%" maxWidth={720}>
              <DiningAvailability
                date={date}
                firstDiningFacilityId={firstDiningFacilityId}
                existingBooking={existingBooking}
              />
            </Box>
          </ScrollView>
        }
        header={
          member?.club ? (
            <TeeSheetAgendaHeaderNew
              isMobileScreen={isMobile ?? false}
              title={existingBooking ? t`Adjust Reservation` : t`Select Time`}
              isScrolled={isScrolled}
              onBackPress={handleBackPress}
              onFilterPress={() => setIsFilterModalOpen(true)}
              variant="DINING"
              clubDesktopRef={member?.club}
              clubMobileRef={member?.club}
              scrollY={scrollY}
              startDate={todayString}
              endDate={calendarEndOn}
              selectedDate={date || todayString}
              onDateChange={(newDate) => {
                navigation.setParams({ date: newDate });
              }}
              clubWeekStartsOn={clubWeekStartsOn}
            />
          ) : (
            <></>
          )
        }
        sidebarContent={
          <VStack gap={4}>
            <VStack p={8} gap={4} bg="surface-secondary" borderRadius="4">
              <Text
                variant="mainHeading"
                testID="facility-selector-dining-agenda-screen"
              >{t`Facility`}</Text>
              <ScheduleSwitcher
                selectedOption={selectedScheduleOption}
                sessionRef={data.session ?? null}
                onChange={onScheduleSwitcherChange}
              />
            </VStack>
            <CalendarBody
              startDate={todayString}
              endDate={calendarEndOn}
              selectedDate={date}
              onSelectDate={(date) => {
                navigation.setParams({
                  date,
                });
              }}
              clubWeekStartsOn={clubWeekStartsOn}
            />
            {date ? (
              <Box backgroundColor="surface-secondary" borderRadius="4" p={8}>
                <DiningAgendaFilters date={date} isMobile={false} />
              </Box>
            ) : null}
          </VStack>
        }
      />
      {isMobile && date ? (
        <DiningAgendaFiltersModal
          isOpen={isFilterModalOpen}
          onClose={() => {
            setIsFilterModalOpen(false);
          }}
          date={date}
        />
      ) : null}
    </>
  );
};

export const DiningAgendaScreenQueryString = graphql`
  query DiningAgendaScreenQuery {
    session {
      ...ScheduleSwitcher_session
      currentClientProfile {
        member {
          club {
            ...TeeSheetAgendaHeaderDesktopNewFragment_club
            ...TeeSheetAgendaHeaderMobileNewFragment_club
            ...useGetClubStartOfWeekNumberPatron_club
            id
            facilities {
              edges {
                node {
                  ...facilityToScheduleOption_facility
                  ...TeeSheetBasedFiltersModalFragment_facility
                  ...AgendaDateSelectorControlFragment_facility
                  id
                  name
                  slug
                  maxMultipleBookings
                  memberBookingWindowDays
                  multipleBookingsEnabled
                  courses {
                    edges {
                      node {
                        name
                        id
                      }
                    }
                  }
                  department {
                    type
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
